import useAgency from 'features/Agency/useAgency';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import ButtonFilterDropdown from 'general/components/quybka/Buttons/ButtonFilterDropdown';
import AppColors from 'general/constants/AppColors';
import AppResource from 'general/constants/AppResource';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import useWindowSize from 'hooks/useWindowSize';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import DialogDriverAddView from '../DialogDriverAddView';
import ChargeDriverListFilterView from './components/ChargeDriverListFilterView';
import ToastHelper from 'general/helpers/ToastHelper';
import * as ExcelJS from 'exceljs';
import AppConstants from 'general/constants/AppConstants';

ChargeDriverListView.propTypes = {};

const defaultColumnIds = [
  'AVATAR',
  'DRIVER_CODE',
  'NAME',
  'OPERATOR',
  'PHONE',
  'EMAIL',
  'STATUS',
  'PLATE',
  'CHARGE_TYPE',
  'TOTAL_EXPENDITURE',
  'WALLET_BALANCE',
  'EKYC_VERIFIED',
  'CREATED_AT',
];

function ChargeDriverListView(props) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { operatorList } = useAgency();
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const prefKey = useMemo(() => {
    const userId = user.userId;
    const pathName = router.pathname;
    const key = `${userId}_${pathName}`;
    return key;
  }, [user, router]);
  const [filter, setFilter] = useState(ChargeHelper.Filter.chargeDriverList);
  const [chargeDriverList, setChargeDriverList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    setIsLoading(true);
    const params = _.pickBy(
      filter,
      (value) => value !== null && value !== undefined && value !== ''
    );
    ChargeHelper.API.Driver.requestGetDriverList(params)
      .then((response) => {
        setChargeDriverList(response?.drivers);
        setTotal(response?.total);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'AVATAR',
        name: UIHelper.buildTableColumn('Ảnh'),
        selector: (row) =>
          UIHelper.buildAvatar(
            row?.avatar ? Utils.getFullUrl(row?.avatar) : null,
            40,
            AppResource.icons.avatar.default,
            false,
            false
          ),
        width: '70px',
      },
      {
        id: 'DRIVER_CODE',
        name: UIHelper.buildTableColumn('Mã khách hàng'),
        selector: (row) => row?.userCode ?? row?.userId,
        width: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'NAME',
        name: UIHelper.buildTableColumn('Họ và tên'),
        selector: (row) => row?.fullname,
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'OPERATOR',
        name: UIHelper.buildTableColumn('Nhà điều hành'),
        selector: (row) => (
          <span className="cursor-pointer hover-opacity-60">
            {_.find(operatorList, (item) => item.operatorId === row?.operatorId)?.name}
          </span>
        ),
        minWidth: '160px',
        style: tableStyle.columns.link,
      },
      {
        id: 'PHONE',
        name: UIHelper.buildTableColumn('Số điện thoại'),
        selector: (row) => row?.phone,
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'EMAIL',
        name: UIHelper.buildTableColumn('Email'),
        selector: (row) => row?.email,
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'EKYC_VERIFIED',
        name: UIHelper.buildTableColumn('Trạng thái xác thực eKYC'),
        selector: (row) => {
          return (
            <KTLabel
              text={row?.ekycVerified ? 'Đã xác thực' : 'Chưa xác thực'}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${row?.ekycVerified ? 'success' : 'secondary'}`}
            />
          );
        },
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PLATE',
        name: UIHelper.buildTableColumn('Biển số xe'),
        selector: (row) => row?.vehicles?.map((item) => item?.plate).join(', '),
        width: '220px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CHARGE_TYPE',
        name: UIHelper.buildTableColumn('Loại giá sạc'),
        selector: (row) => {
          return (
            <KTLabel
              text={ChargeHelper.Utils.getChargePricingTypeName(row?.discountRate)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getChargePricingTypeColor(
                row?.discountRate
              )}`}
            />
          );
        },
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'TOTAL_EXPENDITURE',
        name: UIHelper.buildTableColumn('Tổng chi', 'VNĐ'),
        selector: (row) =>
          Utils.isValid(row?.totalSpend) ? Utils.formatNumber(row?.totalSpend) : '',
        width: '160px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'WALLET_BALANCE',
        name: UIHelper.buildTableColumn('Số dư ví', 'VNĐ'),
        selector: (row) =>
          row?.wallets?.length > 0 ? Utils.formatNumber(_.first(row?.wallets)?.balance) : '',
        width: '160px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS',
        name: UIHelper.buildTableColumn('Trạng thái'),
        selector: (row) => {
          return (
            <KTLabel
              text={ChargeHelper.Utils.getDriverStatusName(row?.status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getDriverStatusColor(
                row?.status
              )}`}
            />
          );
        },
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CREATED_AT',
        name: UIHelper.buildTableColumn('Ngày tạo'),
        selector: (row) => Utils.formatDate(row?.createdAt, 'DD/MM/YYYY - HH:mm'),
        width: '180px',
        style: tableStyle.columns.normal,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        maxWidth: '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          return UIHelper.buildMoreActionsButton(buildMoreActions(row), true);
        },
      },
    ];
  }, [operatorList]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const windowSize = useWindowSize();
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

  // MARK: ---- METHODS ----
  const buildMoreActions = (row) => {
    const actions = [];
    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.driverView.code)) {
      // xem chi tiet
      actions.push({
        text: 'Xem chi tiết',
        iconElement: <i className="fa-regular fa-eye" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          router.navigate(`${row?.userId}`);
        },
      });
    }
    if (isAdmin) {
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.driverUpdate.code)) {
        // kich hoat tai khoan
        if (row?.status === ChargeConstants.ChargeDriverStatus.REGISTERED) {
          actions.push({
            text: 'Kích hoạt tài khoản',
            iconElement: <i className="fa-regular fa-bolt" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.Driver.showDialogConfirmActivateDriver(row, () => {
                handleRefresh();
              });
            },
          });
        }
        // chinh sua
        actions.push({
          text: 'Chỉnh sửa',
          iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            handleAddNew(row);
          },
        });
        if (row?.status === ChargeConstants.ChargeDriverStatus.NORMAL) {
          actions.push({
            text: 'Nạp tiền cho khách hàng',
            iconElement: <i className="fa-regular fa-square-dollar" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.Driver.showDialogTopUpDriver(row, () => {
                handleRefresh();
              });
            },
          });
        }
        actions.push({
          text: 'Đặt giá sạc xe',
          iconElement: <i className="fa-regular fa-money-check-dollar-pen" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.Driver.showDialogSetChargingPrice(row, () => {
              handleRefresh();
            });
          },
        });
        if (row?.status === ChargeConstants.ChargeDriverStatus.NORMAL) {
          actions.push({
            text: 'Ngừng hoạt động',
            iconElement: <i className="fa-regular fa-stop" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.Driver.showDialogConfirmSuspendDriver(row, () => {
                handleRefresh();
              });
            },
          });
        }
        if (row?.status === ChargeConstants.ChargeDriverStatus.SUSPENDED) {
          actions.push({
            text: 'Khôi phục hoạt động',
            iconElement: <i className="fa-regular fa-play" style={{ fontSize: '1.1rem' }} />,
            click: () => {
              ChargeHelper.UI.Driver.showDialogConfirmResumeDriver(row, () => {
                handleRefresh();
              });
            },
          });
        }
        actions.push({
          text: 'Đặt lại mật khẩu',
          iconElement: <i className="fa-regular fa-key" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.Driver.showDialogChangePasswordDriver(row, () => {
              handleRefresh();
            });
          },
        });
      }
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.driverDelete.code)) {
        actions.push({
          text: 'Xóa khách hàng',
          iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.Driver.showDialogConfirmDeleteDriver(row, () => {
              handleRefresh();
            });
          },
        });
      }
    }
    return actions;
  };

  const handleRefresh = () => {
    setFilter({ ...ChargeHelper.Filter.chargeDriverList });
  };
  const handleAddNew = (row) => {
    AppCustomDialogHelper.show(
      !!row ? 'Sửa khách hàng' : 'Thêm khách hàng',
      <DialogDriverAddView
        item={row}
        onCancel={() => AppCustomDialogHelper.hide()}
        onDone={() => {
          AppCustomDialogHelper.hide();
          handleRefresh();
        }}
      />,
      'lg',
      false,
      true
    );
  };

  // xuat file excel
  const handleExportExcel = async (exportData, params) => {
    let exportDataList = exportData;
    if (_.isEmpty(exportDataList)) {
      ToastHelper.showError('Không có dữ liệu để tải xuống');
      return;
    }

    const sheetFileName = 'Danh_sách_khách_hàng_' + moment(params.from).format('DDMMYYYY') + '_' + moment(params.to).format('DDMMYYYY');
    const ext = '.xlsx';
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(sheetFileName, {
      headerFooter: { firstHeader: sheetFileName },
    });

    sheet.mergeCells('A1:O1');
    sheet.getCell('A1').value = `DANH SÁCH KHÁCH HÀNG (NGÀY ${moment(params.from).format(
      'DD/MM/YYYY'
    )} - ${moment(params.to).format('DD/MM/YYYY')})`;
    sheet.getCell('A1').font = {
      name: 'Times New Roman',
      family: 4,
      size: 11,
      bold: true,
      color: { argb: 'FFFFFFFF' },
    };
    sheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
    sheet.getCell('A1').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '29618E' },
      color: { argb: 'FFFFFF' },
    };
    sheet.getCell('A1').border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
    sheet.getRow(1).height = 25;

    const columns = [
      { key: 'stt', width: 7 },
      { key: 'customerCode', width: 18 },
      { key: 'customerName', width: 25 },
      { key: 'cccd', width: 22 },
      { key: 'phone', width: 22 },
      { key: 'email', width: 30 },
      { key: 'ekyc', width: 22 },
      { key: 'plate', width: 22 },
      { key: 'pricingType', width: 15 },
      { key: 'fistBalance', width: 20 },
      { key: 'topupAmount', width: 20 },
      { key: 'chargeAmount', width: 20 },
      { key: 'lastBalance', width: 20 },
      { key: 'status', width: 20 },
      { key: 'date', width: 22 },
    ];
    sheet.columns = columns;

    sheet.getRow(2).height = 30;
    sheet.getRow(2).font = {
      name: 'Times New Roman',
      family: 4,
      size: 11,
      bold: true,
    };
    sheet.getRow(2).alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true,
    };

    sheet.getCell('A2').value = 'STT';
    sheet.getCell('B2').value = 'Mã khách hàng';
    sheet.getCell('C2').value = 'Họ và tên';
    sheet.getCell('D2').value = 'Số CCCD';
    sheet.getCell('E2').value = 'Số điện thoại';
    sheet.getCell('F2').value = 'Email';
    sheet.getCell('G2').value = 'Trạng thái xác thực eKYC';
    sheet.getCell('H2').value = 'Biển số xe';
    sheet.getCell('I2').value = 'Loại giá sạc';
    sheet.getCell('J2').value = 'Số dư ví đầu kỳ (VNĐ)';
    sheet.getCell('K2').value = 'Số tiền nạp trong kỳ (VNĐ)';
    sheet.getCell('L2').value = 'Số tiền sạc trong kỳ (VNĐ)';
    sheet.getCell('M2').value = 'Số dư ví cuối kỳ (VNĐ)';
    sheet.getCell('N2').value = 'Trạng thái';
    sheet.getCell('O2').value = 'Ngày tạo';


    ['A2:O2'].forEach((range) => {
      sheet.getRow(range.split(':')[0].slice(1)).eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    let i = 1;
    exportDataList?.forEach((row) => {
      const dataRow = sheet.addRow({
        stt: i++,
        customerCode: row?.userCode ?? row?.userId,
        customerName: row?.fullname,
        cccd: row?.idCardNumber,
        phone: row?.phone,
        email: row?.email,
        ekyc: row?.ekycVerified ? 'Đã xác thực' : 'Chưa xác thực',
        plate: row?.vehicles?.map((item) => item?.plate).join(', '),
        pricingType: ChargeHelper.Utils.getChargePricingTypeName(row?.discountRate),
        fistBalance: '',
        topupAmount: '',
        chargeAmount: Utils.formatNumber(row?.totalSpend ?? 0),
        lastBalance: row?.wallets?.length > 0 ? Utils.formatNumber(_.first(row?.wallets)?.balance ?? 0) : '',
        status: ChargeHelper.Utils.getDriverStatusName(row?.status),
        date: row?.createdAt ? Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY - HH:mm') : '',
      });

      dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        if (colNumber === 1) {
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        } else if (colNumber >= 10 && colNumber <= 13) {
          cell.alignment = { vertical: 'middle', horizontal: 'right', wrapText: true };
        } else {
          cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        }
      });
    });

    sheet.eachRow((row, rowNumber) => {
      if (rowNumber >= 3) {
        row.font = {
          name: 'Times New Roman',
          family: 4,
          size: 11,
          bold: false,
        };
      }
    });

    workbook.xlsx.writeBuffer().then(function (data) {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = sheetFileName + ext;
      anchor.click();
      window.URL.revokeObjectURL(url);
      ToastHelper.showSuccess('Tải xuống file Excel thành công');
      AppCustomDialogHelper.hide();
    });
  };

  return (
    <div>
      {UIHelper.buildCard(
        <div className="pb-4">
          {/* header */}
          <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
            {/* title */}
            <div className="d-flex flex-column">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                Danh sách khách hàng
              </span>
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                Tổng:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {Utils.formatNumber(total)} khách hàng
                </span>
              </span>
            </div>

            {/* actions */}
            {isAdmin && (
              <div className="d-flex align-items-center justify-content-end gap-3 flex-wrap">
                {UIHelper.buildBlueButton(
                  'Tải xuống',
                  async () => {
                    try {
                      ChargeHelper.UI.Excel.showDialogDownloadExcelFile(
                        ChargeHelper.Filter.chargeDriverList,
                        async (params) => {
                          try {
                            const { drivers = [] } = await ChargeHelper.API.Driver.requestGetDriverList(params);
                            return handleExportExcel(drivers, params);
                          } catch (error) {
                            ToastHelper.showError(`Lỗi khi lấy dữ liệu: ${error.message}`);
                            return [];
                          }
                        },
                      );
                    } catch (error) {
                      ToastHelper.showError(`Lỗi khi khởi tạo tải xuống: ${error.message}`);
                    }
                  },
                  <i className="fa-regular fa-download" />
                )}
                {Utils.checkPermission(permissionList, AppConstants.permissionCode.driverCreate.code) &&
                  UIHelper.buildGreenButton(
                    'Thêm mới',
                    handleAddNew,
                    <i className="fa-regular fa-circle-plus" />
                  )}
              </div>
            )}
          </div>

          {/* table */}
          <div className="">
            {/* filter */}
            <div className="row m-0 p-0 px-2">
              {/* tim kiem */}
              {windowSize.width >= 768 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Tìm kiếm')}
                  <KTFormSearch
                    name="search"
                    placeholder="Theo mã khách hàng, họ và tên..."
                    value={filter.q}
                    onSubmit={(text) => {
                      ChargeHelper.Filter.chargeDriverList = {
                        ...ChargeHelper.Filter.chargeDriverList,
                        q: text,
                        page: 0,
                      };
                      handleRefresh();
                    }}
                  />
                </div>
              )}
              {/* loc theo trang thai */}
              {windowSize.width >= 1120 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Trạng thái')}
                  <KTBSDropdown
                    name="status"
                    toggleElement={
                      <button
                        className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                        type="button"
                      >
                        {_.find(
                          ChargeHelper.Data.chargeDriverStatusOptions,
                          (item) => item.value === ChargeHelper.Filter.chargeDriverList?.status
                        )?.name ?? 'Tất cả'}
                      </button>
                    }
                    selectedValue={ChargeHelper.Filter.chargeDriverList?.status}
                    dropdownMenuItems={[
                      { name: 'Tất cả', value: '' },
                      ...ChargeHelper.Data.chargeDriverStatusOptions,
                    ]}
                    dropdownMenuClassName="overflow-auto w-100"
                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                    onChange={(value) => {
                      ChargeHelper.Filter.chargeDriverList = {
                        ...ChargeHelper.Filter.chargeDriverList,
                        status: value,
                        page: 0,
                      };
                      handleRefresh();
                    }}
                    isDropdownForm={true}
                  />
                </div>
              )}
              {/* thiet lap cot */}
              <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                {/* bo loc */}
                <ButtonFilterDropdown
                  dropdownContent={
                    <ChargeDriverListFilterView
                      onCancel={() => {
                        handleRefresh();
                      }}
                      onApply={() => {
                        handleRefresh();
                      }}
                    />
                  }
                />
                <DropdownTableConfigColumn
                  columnIds={columnIds}
                  fullColumns={fullColumns}
                  fullColumnIds={fullColumnsIdSorted}
                  onChangeCheckboxValue={onChangeCheckboxValue}
                  resetDefaultTableColumn={resetDefaultTableColumn}
                  onChangeFullColumnIds={(ids) => {
                    setFullColumnIds(ids);
                    const key = prefKey + '_FULL_COLUMN_IDS';
                    localStorage.setItem(key, ids.join('|'));
                  }}
                  onSelectAll={onSelectAll}
                  onUnSelectAll={onUnSelectAll}
                  saveChanges={saveChanges}
                />
              </div>
            </div>

            <div className="px-4">
              <KTTable
                columns={columns}
                items={chargeDriverList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                )}
              />
            </div>
          </div>

          {/* pagination */}
          <div className="px-4 mt-2">
            <KTPagination
              currentPage={filter.page + 1}
              rowCount={total}
              rowsPerPage={filter.limit}
              currentItemInPageCount={chargeDriverList?.length}
              onChangePage={(newPage) => {
                const iNewPage = parseInt(newPage);
                ChargeHelper.Filter.chargeDriverList = {
                  ...ChargeHelper.Filter.chargeDriverList,
                  page: iNewPage - 1,
                };
                handleRefresh();
              }}
              onChangeRowsPerPage={(newRowsPerPage) => {
                const iNewRowsPerPage = parseInt(newRowsPerPage);
                ChargeHelper.Filter.chargeDriverList = {
                  ...ChargeHelper.Filter.chargeDriverList,
                  limit: iNewRowsPerPage,
                  page: 0,
                };
                handleRefresh();
              }}
              loading={false}
              color={KTPaginationColor.success}
              appendText="khách hàng"
            />
          </div>
        </div>,
        false
      )}
    </div>
  );
}

export default ChargeDriverListView;
